// src/AGTUps-Product/components/AGTCashFlow-products/Budget.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  LinearProgress,
  FormControlLabel,
  Switch,
  Alert,
  InputAdornment,
  Snackbar,
  Skeleton
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import HelpIcon from '@mui/icons-material/Help';
import {
  BarChart2,
  TrendingUp,
  Plus,
  Edit2,
  Trash2,
  DollarSign,
  Briefcase,
  Smartphone,
  Zap,
  Globe,
  Users,
  Calendar,
  ArrowRight,
  ArrowLeft,
  Download,
  Lock,
  AlertTriangle
} from 'lucide-react';

// Servicio real para presupuestos
import BudgetService from '../../services/AGTUps-product-services/budgetService';

/* ===== ICON MAPPING ===== */
const iconMapping = {
  briefcase: Briefcase,
  smartphone: Smartphone,
  zap: Zap,
  globe: Globe,
  users: Users,
  calendar: Calendar
};

/* ===== COLOR PALETTE ===== */
const appleColors = {
  light: {
    label: { primary: '#000000' },
    system: {
      grey: '#8E8E93',
      grey3: '#C7C7CC',
      green: '#31C859',
      white: '#FFFFFF',
      black: '#000000'
    },
    bg: { primaryElevated: '#FFFFFF' },
    fill: { tertiary: '#767680' },
    ui: { accent: '#007BFE', base: '#F2F2F7' }
  },
  dark: {
    label: { primary: '#FFFFFF' },
    system: {
      grey: '#8E8E93',
      grey3: '#464649',
      green: '#2DD257',
      white: '#FFFFFF',
      black: '#000000'
    },
    bg: { primaryElevated: '#000000' },
    fill: { tertiary: '#767680' },
    ui: { accent: '#0385FF', base: '#272729' }
  }
};

/* ===== ESTILOS COMUNES ===== */
const commonStyles = {
  borderRadius: '16px',
  boxShadow: 'none',
  transition: 'all 0.2s ease-in-out'
};

const dialogPaperStyles = (theme) => ({
  ...commonStyles,
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? 'rgba(128, 128, 128, 0.3)'
    : (theme.palette.mode === 'dark' ? '#1C1C1E' : '#FFFFFF'),
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
  border: '1px solid',
  borderColor: theme.palette.actualMode === 'wallpaper'
    ? 'rgba(128, 128, 128, 0.3)'
    : (theme.palette.mode === 'light'
      ? 'rgba(0,0,0,0.12)'
      : 'rgba(255,255,255,0.12)')
});

/* ===== COMPONENTES ESTILIZADOS ===== */
const PaymentContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  height: 'calc(100vh - 180px)',
  overflowY: 'auto',
  '&::-webkit-scrollbar': { width: '8px' },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(0, 0, 0, 0.2)',
    borderRadius: '4px'
  },
}));

const StyledPaperUnified = styled(Paper)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    padding: theme.spacing(3),
    borderRadius: theme.spacing(2),
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : (theme.palette.mode === 'dark'
        ? appleColors.dark.ui.base
        : appleColors.light.ui.base),
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
    boxShadow: 'none',
  };
});

const AddCategoryButton = styled(Button)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  return {
    backgroundColor: isWallpaperMode ? '#FFFFFF' : '#0385FF',
    color: isWallpaperMode ? '#000000' : '#FFFFFF',
    borderRadius: '30px',
    textTransform: 'none',
    gap: theme.spacing(1),
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.85)' : '#0266cc',
      boxShadow: 'none'
    }
  };
});

/* ===== HOOKS PERSONALIZADOS ===== */
const useSnackbar = () => {
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const hideSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return { snackbar, showSnackbar, hideSnackbar };
};

const useBudgetData = () => {
  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBudgets = async () => {
      try {
        const data = await BudgetService.getBudgets();
        setBudgets(data.data);
      } catch (error) {
        console.error('Error fetching budgets:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBudgets();
  }, []);

  return { budgets, setBudgets, loading };
};

const ProgressWithLabel = ({ value, label, color = 'primary' }) => (
  <Box sx={{ mt: 2 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {value}%
      </Typography>
    </Box>
    <LinearProgress
      variant="determinate"
      value={value}
      color={value > 90 ? 'error' : value > 70 ? 'warning' : color}
    />
  </Box>
);

const BudgetAnalysis = React.memo(({ analysis }) => (
  <StyledPaperUnified sx={{ mb: 3 }}>
    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
      Budget Analysis (Current Month)
    </Typography>
    <Grid container spacing={2}>
      {[
        { label: 'Budgeted Amount', value: analysis.budgeted },
        { label: 'Actual Spent', value: analysis.actualSpent },
        { label: 'Variance', value: analysis.variance, extra: `(${analysis.percentageSpent}%)` }
      ].map(({ label, value, extra }) => (
        <Grid item xs={12} sm={4} key={label}>
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            ${Number(value || 0).toLocaleString()} {extra || ''}
          </Typography>
        </Grid>
      ))}
    </Grid>
    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
      Period: {analysis.period.start} to {analysis.period.end}
    </Typography>
  </StyledPaperUnified>
));

const CategoryActions = ({ isAutoBudget, category, onEdit, onDelete }) => {
  const theme = useTheme();

  if (isAutoBudget) {
    return (
      <Tooltip title="This budget is automatic. To edit categories, only Planned Amount is editable.">
        <Box sx={{ p: 1, backgroundColor: 'rgba(0,0,0,0.3)', borderRadius: 1 }}>
          <Lock size={16} color="#fff" />
        </Box>
      </Tooltip>
    );
  }

  return (
    <>
      <IconButton
        size="small"
        onClick={onEdit}
        sx={{
          color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : 'inherit',
          '&:hover': {
            backgroundColor: theme.palette.actualMode === 'wallpaper'
              ? 'rgba(255, 255, 255, 0.1)'
              : 'rgba(0, 0, 0, 0.04)'
          }
        }}
      >
        <Edit2 size={16} />
      </IconButton>
      <IconButton size="small" color="error" onClick={onDelete}>
        <Trash2 size={16} />
      </IconButton>
    </>
  );
};

/* ===== DIÁLOGOS ===== */

// Diálogo para crear/editar una categoría.
const CategoryFormDialog = ({ open, onClose, category, budgetId, onCategorySaved, isAuto }) => {
  const theme = useTheme();
  const [name, setName] = useState(category?.name || '');
  const [plannedAmount, setPlannedAmount] = useState(category?.budget || '');
  const [spentAmount, setSpentAmount] = useState(category?.spent || 0);
  const [icon, setIcon] = useState(category?.icon || 'briefcase');
  const [error, setError] = useState('');

  useEffect(() => {
    setName(category?.name || '');
    setPlannedAmount(category?.budget || '');
    setSpentAmount(category?.spent || 0);
    setIcon(category?.icon || 'briefcase');
    setError('');
  }, [category, open]);

  const handleSubmit = async () => {
    if (!plannedAmount || Number(plannedAmount) <= 0) {
      setError('Planned Amount must be greater than 0');
      return;
    }
    const categoryData = isAuto
      ? { budget: Number(plannedAmount) }
      : {
          name: name.trim(),
          budget: Number(plannedAmount),
          spent: Number(spentAmount),
          icon
        };

    try {
      let result;
      if (category && category.categoryId) {
        result = await BudgetService.updateCategory(budgetId, category.categoryId, categoryData);
      } else {
        result = await BudgetService.createCategory(budgetId, categoryData);
      }
      onCategorySaved(result.data);
      onClose();
    } catch (err) {
      console.error('Error saving category:', err);
      setError('Failed to save category. Please try again.');
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: dialogPaperStyles(theme)
      }}
    >
      <DialogTitle 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          pb: 2
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {category ? 'Edit Category' : 'Add New Category'}
        </Typography>
        <Tooltip title={category ? 'Modify category details' : 'Create a new expense category'}>
          <IconButton size="small">
            <HelpIcon />
          </IconButton>
        </Tooltip>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Category Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              disabled={isAuto}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Planned Amount"
              type="number"
              value={plannedAmount}
              onChange={(e) => setPlannedAmount(e.target.value)}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <DollarSign size={16} />
                  </InputAdornment>
                )
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Spent Amount"
              type="number"
              value={spentAmount}
              onChange={(e) => setSpentAmount(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <DollarSign size={16} />
                  </InputAdornment>
                )
              }}
              disabled={isAuto}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth disabled={isAuto}>
              <InputLabel
                sx={{
                  color: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? 'rgba(255, 255, 255, 0.7)'
                    : undefined,
                  '&.Mui-focused': {
                    color: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  }
                }}
              >
                Icon
              </InputLabel>
              <Select
                value={icon}
                onChange={(e) => setIcon(e.target.value)}
                label="Icon"
                sx={{
                  color: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? '#FFFFFF'
                    : (theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000'),
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? 'rgba(255, 255, 255, 0.3)'
                      : undefined
                  },
                  '& .MuiSvgIcon-root': {
                    color: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  }
                }}
              >
                <MenuItem value="briefcase">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Briefcase size={16} />
                    Briefcase
                  </Box>
                </MenuItem>
                <MenuItem value="smartphone">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Smartphone size={16} />
                    Smartphone
                  </Box>
                </MenuItem>
                <MenuItem value="zap">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Zap size={16} />
                    Zap
                  </Box>
                </MenuItem>
                <MenuItem value="globe">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Globe size={16} />
                    Globe
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          sx={{
            textTransform: 'none',
            color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#0385FF'
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          sx={{
            textTransform: 'none',
            bgcolor: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#0385FF',
            color: theme.palette.actualMode === 'wallpaper' ? '#000000' : '#FFFFFF',
            borderRadius: '12px',
            boxShadow: 'none',
            '&:hover': {
              bgcolor: theme.palette.actualMode === 'wallpaper'
                ? 'rgba(255, 255, 255, 0.85)'
                : '#0266cc',
              boxShadow: 'none'
            }
          }}
        >
          {category ? 'Save Changes' : 'Create Category'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Diálogo para eliminar categoría
const DeleteCategoryDialog = ({ open, onClose, category, onDelete }) => {
  const theme = useTheme();
  const [deleteConfirmation, setDeleteConfirmation] = useState('');

  const canDelete = deleteConfirmation === category?.name;

  const handleDelete = async () => {
    await onDelete(category);
    setDeleteConfirmation('');
    onClose();
  };

  if (!category) return null;
  
  return (
    <Dialog 
      open={open} 
      onClose={() => { setDeleteConfirmation(''); onClose(); }}
      maxWidth="sm"
      PaperProps={{
        sx: dialogPaperStyles(theme)
      }}
    >
      <DialogTitle 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          pb: 2
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Delete Category
        </Typography>
        <IconButton size="small" onClick={() => { setDeleteConfirmation(''); onClose(); }}>
          <Trash2 size={18} color={theme.palette.error.main} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Alert 
          severity="warning" 
          sx={{ mb: 3, borderRadius: '12px' }}
        >
          This action cannot be undone. All associated budget data will be permanently removed.
        </Alert>

        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to delete this category?
          </Typography>
          <TextField
            fullWidth
            label="Type the category name to confirm"
            value={deleteConfirmation}
            onChange={(e) => setDeleteConfirmation(e.target.value)}
            sx={{ mt: 2 }}
            error={deleteConfirmation !== '' && deleteConfirmation !== category?.name}
            helperText={deleteConfirmation !== '' && deleteConfirmation !== category?.name ? 
              'Name does not match' : ''}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button
          onClick={() => { setDeleteConfirmation(''); onClose(); }}
          sx={{
            textTransform: 'none',
            color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#0385FF'
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          disabled={!canDelete}
          onClick={handleDelete}
          sx={{
            textTransform: 'none',
            borderRadius: '12px',
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' }
          }}
        >
          Delete Category
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Diálogo para ajustes de categorías (globales)
const CategorySettingsDialog = ({ open, onClose }) => {
  const theme = useTheme();
  
  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: dialogPaperStyles(theme)
      }}
    >
      <DialogTitle 
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 2 }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Category Settings
        </Typography>
        <Tooltip title="Configure global category settings">
          <IconButton size="small">
            <HelpIcon />
          </IconButton>
        </Tooltip>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel
                sx={{
                  color: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? 'rgba(255, 255, 255, 0.7)'
                    : undefined,
                  '&.Mui-focused': {
                    color: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  }
                }}
              >
                Budget Rollover
              </InputLabel>
              <Select
                defaultValue="none"
                label="Budget Rollover"
                sx={{
                  borderRadius: '12px',
                  color: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? '#FFFFFF'
                    : (theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000'),
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? 'rgba(255, 255, 255, 0.3)'
                      : undefined
                  },
                  '& .MuiSvgIcon-root': {
                    color: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  }
                }}
              >
                <MenuItem value="none">No rollover</MenuItem>
                <MenuItem value="monthly">Monthly rollover</MenuItem>
                <MenuItem value="quarterly">Quarterly rollover</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Box
              sx={{
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '12px',
                bgcolor: theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.05)'
                  : 'rgba(0, 0, 0, 0.02)'
              }}
            >
              <Typography variant="subtitle2" gutterBottom>
                Alert Settings
              </Typography>
              <FormControlLabel
                control={<Switch defaultChecked />}
                label="Enable budget alerts"
              />
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel
                  sx={{
                    color: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? 'rgba(255, 255, 255, 0.7)'
                      : undefined,
                    '&.Mui-focused': {
                      color: (theme) => theme.palette.actualMode === 'wallpaper'
                        ? '#FFFFFF'
                        : undefined
                    }
                  }}
                >
                  Alert Threshold
                </InputLabel>
                <Select
                  defaultValue="80"
                  label="Alert Threshold"
                  sx={{
                    borderRadius: '12px',
                    color: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : (theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000'),
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                        ? '#FFFFFF'
                        : undefined
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                        ? '#FFFFFF'
                        : undefined
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                        ? 'rgba(255, 255, 255, 0.3)'
                        : undefined
                    },
                    '& .MuiSvgIcon-root': {
                      color: (theme) => theme.palette.actualMode === 'wallpaper'
                        ? '#FFFFFF'
                        : undefined
                    }
                  }}
                >
                  <MenuItem value="70">70% of budget</MenuItem>
                  <MenuItem value="80">80% of budget</MenuItem>
                  <MenuItem value="90">90% of budget</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box
              sx={{
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '12px',
                bgcolor: theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.05)'
                  : 'rgba(0, 0, 0, 0.02)'
              }}
            >
              <Typography variant="subtitle2" gutterBottom>
                Display Settings
              </Typography>
              <FormControl fullWidth>
                <InputLabel
                  sx={{
                    color: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? 'rgba(255, 255, 255, 0.7)'
                      : undefined,
                    '&.Mui-focused': {
                      color: (theme) => theme.palette.actualMode === 'wallpaper'
                        ? '#FFFFFF'
                        : undefined
                    }
                  }}
                >
                  Default View
                </InputLabel>
                <Select
                  defaultValue="monthly"
                  label="Default View"
                  sx={{
                    borderRadius: '12px',
                    color: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : (theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000'),
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                        ? '#FFFFFF'
                        : undefined
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                        ? '#FFFFFF'
                        : undefined
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                        ? 'rgba(255, 255, 255, 0.3)'
                        : undefined
                    },
                    '& .MuiSvgIcon-root': {
                      color: (theme) => theme.palette.actualMode === 'wallpaper'
                        ? '#FFFFFF'
                        : undefined
                    }
                  }}
                >
                  <MenuItem value="monthly">Monthly</MenuItem>
                  <MenuItem value="quarterly">Quarterly</MenuItem>
                  <MenuItem value="yearly">Yearly</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          sx={{
            textTransform: 'none',
            color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#0385FF'
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          sx={{
            textTransform: 'none',
            bgcolor: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#0385FF',
            color: theme.palette.actualMode === 'wallpaper' ? '#000000' : '#FFFFFF',
            borderRadius: '12px',
            boxShadow: 'none',
            '&:hover': {
              bgcolor: theme.palette.actualMode === 'wallpaper'
                ? 'rgba(255, 255, 255, 0.85)'
                : '#0266cc',
              boxShadow: 'none'
            }
          }}
        >
          Save Settings
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Diálogo para actualizar el total del presupuesto
const UpdateTotalBudgetDialog = ({ open, onClose, budget, onBudgetUpdated }) => {
  const theme = useTheme();
  const [newTotal, setNewTotal] = useState(budget.totalBudget);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (budget) {
      setNewTotal(budget.totalBudget);
      setError('');
    }
  }, [budget, open]);

  const handleUpdate = async () => {
    if (!newTotal || Number(newTotal) <= 0) {
      setError("Budget must be greater than 0");
      return;
    }
    setSubmitting(true);
    try {
      const updatedBudget = await BudgetService.updateBudget(budget.budgetId, { totalBudget: Number(newTotal) });
      onBudgetUpdated(updatedBudget.data);
      onClose();
    } catch (e) {
      console.error("Error updating total budget", e);
      setError("Failed to update total budget");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      fullWidth 
      maxWidth="sm"
      PaperProps={{
        sx: dialogPaperStyles(theme)
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems:'center', justifyContent: 'space-between', pb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>Update Total Budget</Typography>
      </DialogTitle>
      <DialogContent sx={{ pt:2 }}>
        {error && (
          <Alert severity="error" sx={{ mb:2 }}>{error}</Alert>
        )}
        <TextField 
          fullWidth 
          label="Total Budget" 
          type="number" 
          value={newTotal}
          onChange={(e) => setNewTotal(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <DollarSign size={16} />
              </InputAdornment>
            )
          }}
        />
      </DialogContent>
      <DialogActions sx={{ p:2, gap:1 }}>
         <Button
           onClick={onClose}
           sx={{
             textTransform:'none',
             color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#0385FF'
           }}
         >
           Cancel
         </Button>
         <Button
           variant="contained"
           sx={{
             textTransform:'none',
             bgcolor: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#0385FF',
             color: theme.palette.actualMode === 'wallpaper' ? '#000000' : '#FFFFFF',
             borderRadius:'12px',
             boxShadow:'none',
             '&:hover': {
               bgcolor: theme.palette.actualMode === 'wallpaper'
                 ? 'rgba(255, 255, 255, 0.85)'
                 : '#0266cc',
               boxShadow:'none'
             }
           }}
           onClick={handleUpdate}
           disabled={submitting}
         >
           Save
         </Button>
      </DialogActions>
    </Dialog>
  );
};

// Diálogo para crear un nuevo presupuesto
const newBudgetIconOptions = [
  { icon: 'briefcase', label: 'Briefcase', color: '#4f46e5' },
  { icon: 'users', label: 'Users', color: '#0891b2' },
  { icon: 'calendar', label: 'Calendar', color: '#059669' },
  { icon: 'globe', label: 'Global', color: '#7c3aed' }
];

const NewBudgetDialog = ({ open, onClose, onCreate }) => {
  const theme = useTheme();
  const [budgetName, setBudgetName] = useState('');
  const [budgetDescription, setBudgetDescription] = useState('');
  const [totalBudget, setTotalBudget] = useState('');
  const [selectedIcon, setSelectedIcon] = useState(0);
  const [formErrors, setFormErrors] = useState({ name: '', description: '', totalBudget: '' });
  const [submitting, setSubmitting] = useState(false);

  const validateForm = () => {
    const errors = {};
    if (!budgetName.trim()) errors.name = 'Name is required';
    if (!budgetDescription.trim()) errors.description = 'Description is required';
    if (!totalBudget || totalBudget <= 0) errors.totalBudget = 'Budget must be greater than 0';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreate = async () => {
    if (!validateForm()) return;
    setSubmitting(true);
    const option = newBudgetIconOptions[selectedIcon];
    const newBudget = {
      name: budgetName,
      description: budgetDescription,
      totalBudget: Number(totalBudget),
      categories: [],
      icon: option.icon,
      color: option.color
    };
    try {
      const createdBudget = await BudgetService.createBudget(newBudget);
      onCreate(createdBudget);
      onClose();
      setBudgetName('');
      setBudgetDescription('');
      setTotalBudget('');
      setSelectedIcon(0);
    } catch (error) {
      console.error('Error creating budget:', error);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      fullWidth 
      maxWidth="sm"
      PaperProps={{
        sx: dialogPaperStyles(theme)
      }}
    >
      <DialogTitle 
        sx={{ display: 'flex', alignItems:'center', justifyContent: 'space-between', pb: 2 }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600 }}>New Budget</Typography>
        <IconButton size="small" onClick={onClose}>
          <Trash2 size={18} color={theme.palette.error.main} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt:2 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Budget Name"
              value={budgetName}
              onChange={(e) => setBudgetName(e.target.value)}
              error={!!formErrors.name}
              helperText={formErrors.name}
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: theme.palette.actualMode === 'wallpaper'
                    ? 'rgba(128, 128, 128, 0.25)'
                    : 'transparent',
                  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
                  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
                  '& fieldset': {
                    borderColor: theme.palette.actualMode === 'wallpaper'
                      ? 'rgba(128, 128, 128, 0.3)'
                      : undefined
                  },
                  '&:hover fieldset': {
                    borderColor: theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  }
                },
                '& .MuiInputLabel-root': {
                  color: theme.palette.actualMode === 'wallpaper'
                    ? 'rgba(255, 255, 255, 0.7)'
                    : undefined,
                  '&.Mui-focused': {
                    color: theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  }
                },
                '& .MuiInputBase-input': {
                  color: theme.palette.actualMode === 'wallpaper'
                    ? '#FFFFFF'
                    : undefined
                }
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              value={budgetDescription}
              onChange={(e) => setBudgetDescription(e.target.value)}
              error={!!formErrors.description}
              helperText={formErrors.description}
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: theme.palette.actualMode === 'wallpaper'
                    ? 'rgba(128, 128, 128, 0.25)'
                    : 'transparent',
                  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
                  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
                  '& fieldset': {
                    borderColor: theme.palette.actualMode === 'wallpaper'
                      ? 'rgba(128, 128, 128, 0.3)'
                      : undefined
                  },
                  '&:hover fieldset': {
                    borderColor: theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  }
                },
                '& .MuiInputLabel-root': {
                  color: theme.palette.actualMode === 'wallpaper'
                    ? 'rgba(255, 255, 255, 0.7)'
                    : undefined,
                  '&.Mui-focused': {
                    color: theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  }
                },
                '& .MuiInputBase-input': {
                  color: theme.palette.actualMode === 'wallpaper'
                    ? '#FFFFFF'
                    : undefined
                }
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Tooltip title="The total budget will be allocated among the categories you create">
              <TextField
                fullWidth
                label="Total Budget"
                type="number"
                value={totalBudget}
                onChange={(e) => setTotalBudget(e.target.value)}
                error={!!formErrors.totalBudget}
                helperText={formErrors.totalBudget}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <DollarSign
                        size={16}
                        color={theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : undefined}
                      />
                    </InputAdornment>
                  )
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: theme.palette.actualMode === 'wallpaper'
                      ? 'rgba(128, 128, 128, 0.25)'
                      : 'transparent',
                    backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
                    WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
                    '& fieldset': {
                      borderColor: theme.palette.actualMode === 'wallpaper'
                        ? 'rgba(128, 128, 128, 0.3)'
                        : undefined
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.actualMode === 'wallpaper'
                        ? '#FFFFFF'
                        : undefined
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.actualMode === 'wallpaper'
                        ? '#FFFFFF'
                        : undefined
                    }
                  },
                  '& .MuiInputLabel-root': {
                    color: theme.palette.actualMode === 'wallpaper'
                      ? 'rgba(255, 255, 255, 0.7)'
                      : undefined,
                    '&.Mui-focused': {
                      color: theme.palette.actualMode === 'wallpaper'
                        ? '#FFFFFF'
                        : undefined
                    }
                  },
                  '& .MuiInputBase-input': {
                    color: theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  }
                }}
              />
            </Tooltip>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel
                sx={{
                  color: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? 'rgba(255, 255, 255, 0.7)'
                    : undefined,
                  '&.Mui-focused': {
                    color: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  }
                }}
              >
                Icon
              </InputLabel>
              <Select
                value={selectedIcon}
                onChange={(e) => setSelectedIcon(e.target.value)}
                label="Icon"
                sx={{
                  color: (theme) => theme.palette.actualMode === 'wallpaper'
                    ? '#FFFFFF'
                    : (theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000'),
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? 'rgba(255, 255, 255, 0.3)'
                      : undefined
                  },
                  '& .MuiSvgIcon-root': {
                    color: (theme) => theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : undefined
                  }
                }}
              >
                {newBudgetIconOptions.map((option, index) => (
                  <MenuItem value={index} key={index}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          backgroundColor: option.color,
                          p: 1,
                          borderRadius: 1,
                          color: 'white'
                        }}
                      >
                        {React.createElement(iconMapping[option.icon])}
                      </Box>
                      {option.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p:2, gap:1 }}>
         <Button
           onClick={onClose}
           sx={{
             textTransform:'none',
             color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#0385FF'
           }}
         >
           Cancel
         </Button>
         <Button
           variant="contained"
           sx={{
             textTransform:'none',
             bgcolor: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#0385FF',
             color: theme.palette.actualMode === 'wallpaper' ? '#000000' : '#FFFFFF',
             borderRadius:'12px',
             boxShadow:'none',
             '&:hover': {
               bgcolor: theme.palette.actualMode === 'wallpaper'
                 ? 'rgba(255, 255, 255, 0.85)'
                 : '#0266cc',
               boxShadow:'none'
             }
           }}
           onClick={handleCreate}
           disabled={submitting}
         >
           Create Budget
         </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ===== COMPONENTES DEL RESUMEN Y TARJETAS ===== */

/* 
  COMPONENTES REDISEÑADOS CON ESTILO MODERNO Y MINIMALISTA AL ESTILO APPLE
*/

// BudgetCard mejorado con diseño más moderno
const BudgetCard = ({ budget, onSelect }) => {
  const theme = useTheme();
  const IconComponent = iconMapping[budget.icon] || Briefcase;

  return (
    <Paper
      sx={{
        p: 3,
        borderRadius: '24px',
        backgroundColor: theme.palette.actualMode === 'wallpaper'
          ? 'rgba(128, 128, 128, 0.3)'
          : 'background.paper',
        backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
        WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
        border: '1px solid',
        borderColor: theme.palette.actualMode === 'wallpaper'
          ? 'rgba(128, 128, 128, 0.3)'
          : 'divider',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: '280px',
        position: 'relative',
        overflow: 'hidden',
        cursor: 'pointer'
      }}
      elevation={0}
      onClick={() => onSelect(budget)}
    >
      {/* Círculo decorativo de fondo */}
      <Box
        sx={{
          position: 'absolute',
          top: '-20%',
          right: '-20%',
          width: '200px',
          height: '200px',
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${budget.color}20, ${budget.color}40)`,
          zIndex: 0
        }}
      />

      {/* Contenido principal */}
      <Box sx={{ position: 'relative', zIndex: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <Box
            sx={{
              backgroundColor: budget.color,
              p: 2,
              borderRadius: '16px',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <IconComponent size={24} />
          </Box>
          
          {(budget.budgetType && budget.budgetType.startsWith('auto_')) && (
            <Box sx={{ 
              p: 1, 
              borderRadius: '12px',
              backgroundColor: 'rgba(255, 193, 7, 0.1)',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <Lock size={16} color="#FFC107" />
              <Typography variant="caption" color="warning.main">Auto</Typography>
            </Box>
          )}
        </Box>

        <Typography variant="h5" sx={{
          fontWeight: 700,
          mb: 1,
          color: theme.palette.actualMode === 'wallpaper'
            ? '#FFFFFF'
            : 'text.primary'
        }}>
          {budget.name}
        </Typography>

        <Typography variant="body2" sx={{
          color: theme.palette.actualMode === 'wallpaper'
            ? 'rgba(255, 255, 255, 0.8)'
            : 'text.secondary',
          mb: 3,
          lineHeight: 1.6
        }}>
          {budget.description || 'No description provided'}
        </Typography>

        <Box sx={{ mt: 'auto' }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="overline" sx={{
              color: theme.palette.actualMode === 'wallpaper'
                ? 'rgba(255, 255, 255, 0.7)'
                : 'text.secondary',
              letterSpacing: 1
            }}>
              Total Budget
            </Typography>
            <Typography variant="h4" sx={{
              fontWeight: 700,
              color: theme.palette.actualMode === 'wallpaper'
                ? '#FFFFFF'
                : budget.color,
              letterSpacing: '-0.5px'
            }}>
              ${Number(budget.totalBudget || 0).toLocaleString()}
            </Typography>
          </Box>

          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.actualMode === 'wallpaper'
                  ? 'rgba(255, 255, 255, 0.7)'
                  : 'text.secondary'
              }}
            >
              {budget.categories?.length || 0} categories
            </Typography>
            <Button
              endIcon={<ArrowRight size={16} />}
              sx={{
                textTransform: 'none',
                backgroundColor: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : 'transparent',
                color: theme.palette.actualMode === 'wallpaper' ? '#000000' : budget.color,
                boxShadow: 'none',
                borderRadius: '12px',
                px: 2,
                py: 1,
                '&:hover': {
                  backgroundColor: theme.palette.actualMode === 'wallpaper'
                    ? 'rgba(255, 255, 255, 0.85)'
                    : `${budget.color}10`,
                  boxShadow: 'none'
                }
              }}
              onClick={(e) => {
                e.stopPropagation();
                onSelect(budget);
              }}
            >
              View Details
            </Button>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

// NewBudgetCard mejorado con diseño más moderno
const NewBudgetCard = ({ onClick }) => {
  const theme = useTheme();

  return (
  <Paper
    sx={{
      p: 3,
      borderRadius: '24px',
      height: '100%',
      minHeight: '280px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      position: 'relative',
      overflow: 'hidden',
      border: '1px solid',
      borderColor: theme.palette.actualMode === 'wallpaper'
        ? 'rgba(128, 128, 128, 0.3)'
        : 'divider',
      backgroundColor: theme.palette.actualMode === 'wallpaper'
        ? 'rgba(128, 128, 128, 0.3)'
        : 'background.paper',
      backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
      WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
      transition: 'background-color 0.3s ease'
    }}
    elevation={0}
    onClick={onClick}
  >
    {/* Círculos decorativos de fondo */}
    <Box
      sx={{
        position: 'absolute',
        top: '-10%',
        right: '-10%',
        width: '150px',
        height: '150px',
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #0385FF10, #0385FF20)',
        zIndex: 0
      }}
    />
    <Box
      sx={{
        position: 'absolute',
        bottom: '-15%',
        left: '-15%',
        width: '180px',
        height: '180px',
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #0385FF05, #0385FF15)',
        zIndex: 0
      }}
    />

    {/* Contenido principal */}
    <Box
      sx={{
        position: 'relative',
        zIndex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center'
      }}
    >
      <Box
        sx={{
          backgroundColor: theme.palette.actualMode === 'wallpaper'
            ? 'rgba(255, 255, 255, 0.1)'
            : '#0385FF10',
          p: 3,
          borderRadius: '20px',
          mb: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Plus
          size={32}
          style={{
            color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#0385FF'
          }}
        />
      </Box>
      
      <Typography variant="h5" sx={{
        fontWeight: 700,
        color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#0385FF',
        mb: 2
      }}>
        Create New Budget
      </Typography>
      
      <Typography variant="body2" sx={{
        color: theme.palette.actualMode === 'wallpaper'
          ? 'rgba(255, 255, 255, 0.8)'
          : 'text.secondary',
        maxWidth: '240px',
        lineHeight: 1.6
      }}>
        Start managing your expenses by creating a new customized budget
      </Typography>
    </Box>
  </Paper>
  );
};

/* ===== COMPONENTE PRINCIPAL: ExpenseCategories ===== */
const ExpenseCategories = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { budgets, setBudgets, loading } = useBudgetData();
  const { snackbar, showSnackbar, hideSnackbar } = useSnackbar();
  const [selectedBudget, setSelectedBudget] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [analysis, setAnalysis] = useState(null);

  // Estado centralizado para diálogos
  const [dialogs, setDialogs] = useState({
    category: false,
    delete: false,
    settings: false,
    alerts: false,
    newBudget: false,
    updateBudget: false
  });

  // Funciones para abrir y cerrar diálogos
  const openDialog = (name) => setDialogs(prev => ({ ...prev, [name]: true }));
  const closeDialog = (name) => setDialogs(prev => ({ ...prev, [name]: false }));

  useEffect(() => {
    if (selectedBudget) {
      const fetchAnalysis = async () => {
        try {
          const result = await BudgetService.getBudgetAnalysis(selectedBudget.budgetId);
          setAnalysis(result.data.analysis);
        } catch (error) {
          console.error('Error fetching budget analysis:', error);
        }
      };
      fetchAnalysis();
    }
  }, [selectedBudget]);

  if (!selectedBudget) {
    return (
      <PaymentContainer>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
            Your Budgets
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Select an existing budget or create a new one
          </Typography>
        </Box>

        {loading ? (
          <Skeleton variant="rectangular" height={200} />
        ) : (
          <Grid container spacing={3}>
            {budgets && budgets.length > 0 ? (
              budgets.map((budget) => (
                <Grid item xs={12} sm={6} md={4} key={budget.budgetId}>
                  <BudgetCard
                    budget={budget}
                    onSelect={(b) => setSelectedBudget(b)}
                  />
                </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                <Typography variant="body1" color="text.secondary">
                  No budgets found.
                </Typography>
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={4}>
              <NewBudgetCard onClick={() => openDialog('newBudget')} />
            </Grid>
          </Grid>
        )}

        <NewBudgetDialog 
          open={dialogs.newBudget}
          onClose={() => closeDialog('newBudget')}
          onCreate={(newBudget) => {
            setBudgets([...budgets, newBudget]);
            setSelectedBudget(newBudget);
            showSnackbar('New budget created successfully', 'success');
          }}
        />
        <Snackbar 
          open={snackbar.open} 
          autoHideDuration={6000} 
          onClose={hideSnackbar}
        >
          <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </PaymentContainer>
    );
  }

  const handleBack = () => {
    setSelectedBudget(null);
    setAnalysis(null);
  };

  const isAutoBudget = selectedBudget.budgetType && selectedBudget.budgetType.startsWith('auto_');

  const handleDeleteCategory = async (categoryToDelete) => {
    try {
      const result = await BudgetService.deleteCategory(selectedBudget.budgetId, categoryToDelete.categoryId);
      setSelectedBudget(result.data);
      setBudgets(budgets.map(b => b.budgetId === result.data.budgetId ? result.data : b));
      showSnackbar('Category deleted successfully', 'info');
    } catch (error) {
      console.error('Error deleting category:', error);
      showSnackbar('Failed to delete category', 'error');
    }
  };

  const handleCategorySaved = (updatedBudget) => {
    setSelectedBudget(updatedBudget);
    setBudgets(budgets.map(b => b.budgetId === updatedBudget.budgetId ? updatedBudget : b));
    showSnackbar('Category saved successfully', 'success');
  };

  const handleDeleteBudget = async () => {
    try {
      await BudgetService.deleteBudget(selectedBudget.budgetId);
      setBudgets(budgets.filter(b => b.budgetId !== selectedBudget.budgetId));
      setSelectedBudget(null);
      showSnackbar('Budget deleted successfully', 'info');
    } catch (error) {
      console.error('Error deleting budget:', error);
      showSnackbar('Failed to delete budget', 'error');
    }
  };

  return (
    <PaymentContainer>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          p: 2
        }}
      >
        <Button
          startIcon={<ArrowLeft size={16} />}
          onClick={handleBack}
          sx={{
            textTransform: 'none',
            color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#0385FF',
            '&:hover': {
              backgroundColor: theme.palette.actualMode === 'wallpaper'
                ? 'rgba(255, 255, 255, 0.1)'
                : 'rgba(3, 133, 255, 0.08)'
            }
          }}
        >
          {!isMobile && 'Back to Budgets'}
        </Button>
        <Box sx={{ flex: 1, textAlign: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {selectedBudget.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {selectedBudget.description}
          </Typography>
        </Box>
        <Box>
          {!isAutoBudget && (
            <Button
              startIcon={<Trash2 size={16} />}
              onClick={handleDeleteBudget}
              sx={{ textTransform: 'none', color: 'red' }}
            >
              {!isMobile && 'Delete Budget'}
            </Button>
          )}
          <Button
            onClick={() => { /* Implement export if needed */ }}
            startIcon={<Download size={16} />}
            sx={{
              textTransform: 'none',
              ml: 2,
              color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : undefined,
              '&:hover': {
                backgroundColor: theme.palette.actualMode === 'wallpaper'
                  ? 'rgba(255, 255, 255, 0.1)'
                  : undefined
              }
            }}
          >
            {!isMobile && 'Export'}
          </Button>
        </Box>
      </Box>

      <BudgetSummaryCards 
        budget={selectedBudget} 
        onEditTotalBudget={() => openDialog('updateBudget')}
      />

      {analysis && <BudgetAnalysis analysis={analysis} />}

      <StyledPaperUnified sx={{ mt: 3 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }} gutterBottom>
            Payments
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Showing {selectedBudget.categories?.length || 0} expense categories linked to this budget.
          </Typography>
        </Box>
        {selectedBudget.categories?.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary">
              No categories found.
            </Typography>
          </Box>
        ) : (
          selectedBudget.categories.map(category => {
            const usagePercent = selectedBudget.totalBudget > 0 
              ? Math.round(((category.spent || 0) / selectedBudget.totalBudget) * 100)
              : 0;
            const catSpentPercentage = category.budget > 0
              ? Math.round((category.spent || 0) / category.budget * 100)
              : 0;
            
            return (
              <StyledPaperUnified key={category.categoryId} sx={{ mb: 2, p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    <Box sx={{ backgroundColor: category.color, p: 1, borderRadius: 1, color: 'white' }}>
                      {React.createElement(iconMapping[category.icon] || Briefcase, { size: 24 })}
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {category.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Planned: ${Number(category.budget || 0).toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      ${Number(category.spent || 0).toLocaleString()}
                    </Typography>
                    <CategoryActions 
                      isAutoBudget={isAutoBudget}
                      category={category}
                      onEdit={() => {
                        setSelectedCategory(category);
                        openDialog('category');
                      }}
                      onDelete={() => {
                        setSelectedCategory(category);
                        openDialog('delete');
                      }}
                    />
                  </Box>
                </Box>

                <ProgressWithLabel 
                  value={usagePercent} 
                  label="Cost % of Total Budget" 
                />
                <ProgressWithLabel 
                  value={catSpentPercentage} 
                  label="Category Spent vs Planned" 
                />
              </StyledPaperUnified>
            );
          })
        )}
        {!isAutoBudget && (
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <AddCategoryButton onClick={() => {
              setSelectedCategory(null);
              openDialog('category');
            }}>
              <Plus size={16} /> Add New Category
            </AddCategoryButton>
          </Box>
        )}
      </StyledPaperUnified>

      <UpdateTotalBudgetDialog 
        open={dialogs.updateBudget}
        onClose={() => closeDialog('updateBudget')}
        budget={selectedBudget}
        onBudgetUpdated={(updatedBudget) => {
          setSelectedBudget(updatedBudget);
          setBudgets(budgets.map(b => b.budgetId === updatedBudget.budgetId ? updatedBudget : b));
          showSnackbar('Total budget updated successfully', 'success');
        }}
      />

      <CategoryFormDialog 
        open={dialogs.category}
        onClose={() => {
          closeDialog('category');
          setSelectedCategory(null);
        }}
        category={selectedCategory}
        budgetId={selectedBudget.budgetId}
        onCategorySaved={handleCategorySaved}
        isAuto={isAutoBudget}
      />
    
      <DeleteCategoryDialog 
        open={dialogs.delete}
        onClose={() => closeDialog('delete')}
        category={selectedCategory}
        onDelete={handleDeleteCategory}
      />

      <CategorySettingsDialog 
        open={dialogs.settings}
        onClose={() => closeDialog('settings')}
      />

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={hideSnackbar}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PaymentContainer>
  );
};

const BudgetSummaryCards = ({ budget, onEditTotalBudget }) => {
  const theme = useTheme();
  const totalSpent = budget.categories?.reduce((sum, cat) => sum + (cat.spent || 0), 0) || 0;
  const percentage = Number(budget.totalBudget) > 0 ? Math.round((totalSpent / budget.totalBudget) * 100) : 0;
  return (
    <Grid container spacing={3} sx={{ mb: 3 }}>
      <Grid item xs={12} sm={4}>
        <StyledPaperUnified>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <DollarSign
              size={20}
              style={{
                color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#0385FF'
              }}
            />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Total Budget</Typography>
            <IconButton
              size="small"
              onClick={onEditTotalBudget}
              sx={{
                color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : 'inherit',
                '&:hover': {
                  backgroundColor: theme.palette.actualMode === 'wallpaper'
                    ? 'rgba(255, 255, 255, 0.1)'
                    : 'rgba(0, 0, 0, 0.04)'
                }
              }}
            >
              <Edit2 size={16} />
            </IconButton>
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            ${Number(budget.totalBudget || 0).toLocaleString()}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {budget.categories?.length || 0} active categories
          </Typography>
        </StyledPaperUnified>
      </Grid>
      <Grid item xs={12} sm={4}>
        <StyledPaperUnified>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <BarChart2 size={20} style={{ color: '#2DD257' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Total Spent</Typography>
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            ${Number(totalSpent).toLocaleString()}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrendingUp size={16} style={{ color: '#31C859' }} />
            <Typography variant="body2" color="success.main">
              {percentage}% of budget
            </Typography>
          </Box>
        </StyledPaperUnified>
      </Grid>
      <Grid item xs={12} sm={4}>
        <StyledPaperUnified>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
                borderRadius: '50%',
                backgroundColor: (theme) => theme.palette.actualMode === 'wallpaper'
                  ? 'rgba(255, 193, 7, 0.2)'
                  : 'rgba(255, 193, 7, 0.1)'
              }}
            >
              <AlertTriangle size={20} style={{ color: '#FFC107' }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Alerts</Typography>
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {budget.categories?.filter(cat => ((cat.spent || 0) / (cat.budget || 1)) > 0.8).length || 0}
          </Typography>
          <Typography variant="body2" color="warning.main">
            Categories near limit
          </Typography>
        </StyledPaperUnified>
      </Grid>
    </Grid>
  );
};

export default ExpenseCategories;
